import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { CText, CLoader } from '../../components/common';
import { IoArrowBack, IoBusinessOutline, IoDocumentTextOutline, IoStatsChartOutline, IoPeopleOutline } from 'react-icons/io5';
import { FiExternalLink } from 'react-icons/fi';
import { getCompanyDetails, CompanyDetails } from '../../api/copilot';

type TabType = 'overview' | 'financials' | 'management' | 'documents';

const CompanyDetail: React.FC = () => {
  const { companyName } = useParams<{ companyName: string }>();
  const navigate = useNavigate();
  const colors = useAppSelector((state) => state.theme.theme);

  const [company, setCompany] = useState<CompanyDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('overview');

  const decodedCompanyName = companyName ? decodeURIComponent(companyName) : '';

  useEffect(() => {
    const loadCompanyDetails = async () => {
      if (!decodedCompanyName) return;

      try {
        setLoading(true);
        setError(null);
        const result = await getCompanyDetails(decodedCompanyName);
        if (result.status === 'success' && result.data) {
          setCompany(result.data);
        } else {
          setError(result.error || 'Company not found');
        }
      } catch (err) {
        console.error('Failed to fetch company details:', err);
        setError('Failed to load company data');
      } finally {
        setLoading(false);
      }
    };
    loadCompanyDetails();
  }, [decodedCompanyName]);

  const formatValue = (value: any): string => {
    if (value === null || value === undefined || value === '-') return '-';
    if (typeof value === 'number') {
      if (value >= 10000000) return `${(value / 10000000).toFixed(2)} Cr`;
      if (value >= 100000) return `${(value / 100000).toFixed(2)} L`;
      return value.toLocaleString();
    }
    return String(value);
  };

  const tabs = [
    { id: 'overview' as TabType, label: 'Overview', icon: IoBusinessOutline },
    { id: 'financials' as TabType, label: 'Financials', icon: IoStatsChartOutline },
    { id: 'management' as TabType, label: 'Management', icon: IoPeopleOutline },
    { id: 'documents' as TabType, label: 'Documents', icon: IoDocumentTextOutline },
  ];

  const renderMetricsCard = () => {
    if (!company) return null;

    const metrics = [
      { label: 'Current Price', value: formatValue(company.current_price), prefix: '₹' },
      { label: 'Market Cap', value: formatValue(company.market_cap) },
      { label: 'P/E Ratio', value: formatValue(company.stock_pe) },
      { label: 'Book Value', value: formatValue(company.book_value) },
      { label: 'ROE', value: company.roe ? `${company.roe.toFixed(2)}%` : '-' },
      { label: 'ROCE', value: company.roce ? `${company.roce.toFixed(2)}%` : '-' },
      { label: 'Dividend Yield', value: company.dividend_yield ? `${company.dividend_yield.toFixed(2)}%` : '-' },
      { label: 'Face Value', value: formatValue(company.face_value) },
    ];

    return (
      <div
        className="p-4 rounded-xl mb-4"
        style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.borderColor}` }}
      >
        <CText type="M14" color={colors.grayScale500} style={{ marginBottom: 12 }}>
          Key Metrics
        </CText>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {metrics.map((metric) => (
            <div key={metric.label}>
              <CText type="r12" color={colors.grayScale400}>{metric.label}</CText>
              <CText type="B16" color={colors.textColor}>
                {metric.prefix || ''}{metric.value}
              </CText>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderInsightSection = (title: string, content: string | undefined) => {
    if (!content) return null;
    return (
      <div className="mb-4">
        <CText type="M14" color={colors.primary} style={{ marginBottom: 8 }}>
          {title}
        </CText>
        <div
          className="p-3 rounded-lg"
          style={{ backgroundColor: colors.backgroundColor, border: `1px solid ${colors.borderColor}` }}
        >
          <CText type="r14" color={colors.textColor} style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
            {content}
          </CText>
        </div>
      </div>
    );
  };

  const renderOverviewTab = () => {
    const insights = company?.company_insights;
    if (!insights) {
      return (
        <div className="text-center py-8">
          <CText type="M14" color={colors.grayScale500}>
            No business insights available for this company
          </CText>
        </div>
      );
    }

    return (
      <div>
        {renderInsightSection('Business Overview', insights.business_overview)}
        {renderInsightSection('Product Segments', insights.product_segments)}
        {renderInsightSection('Future Plans', insights.future_plans)}
        {renderInsightSection('Clientele', insights.clientele)}
        {renderInsightSection('Suppliers', insights.suppliers)}
        {renderInsightSection('Joint Ventures', insights.joint_ventures)}
        {renderInsightSection('Subsidiaries', insights.subsidiaries)}
      </div>
    );
  };

  const renderFinancialTable = (title: string, data: any) => {
    if (!data) return null;

    // Parse data if it's a string
    let parsedData = data;
    if (typeof data === 'string') {
      try {
        parsedData = JSON.parse(data);
      } catch {
        return null;
      }
    }

    // Handle array of objects
    if (!Array.isArray(parsedData) || parsedData.length === 0) return null;

    const headers = Object.keys(parsedData[0]).slice(0, 6); // Limit columns

    return (
      <div className="mb-6">
        <CText type="M14" color={colors.primary} style={{ marginBottom: 12 }}>
          {title}
        </CText>
        <div className="overflow-x-auto rounded-lg" style={{ border: `1px solid ${colors.borderColor}` }}>
          <table className="w-full text-sm">
            <thead>
              <tr style={{ backgroundColor: colors.primary }}>
                {headers.map((header) => (
                  <th key={header} className="px-3 py-2 text-left text-white font-medium">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {parsedData.slice(0, 8).map((row: any, idx: number) => (
                <tr
                  key={idx}
                  style={{
                    backgroundColor: idx % 2 === 0 ? colors.backgroundColor : colors.inputBackground,
                    borderBottom: `1px solid ${colors.borderColor}`,
                  }}
                >
                  {headers.map((header) => (
                    <td key={header} className="px-3 py-2">
                      <CText type="r12" color={colors.textColor}>
                        {formatValue(row[header])}
                      </CText>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderFinancialsTab = () => {
    if (!company) return null;

    return (
      <div>
        {renderFinancialTable('Quarterly Results', company.quarterly_results_data)}
        {renderFinancialTable('Yearly Results', company.yearly_results_data)}
        {renderFinancialTable('Balance Sheet', company.balance_sheets)}
        {renderFinancialTable('Cash Flow', company.cashflow_statements)}
        {!company.quarterly_results_data && !company.yearly_results_data && !company.balance_sheets && !company.cashflow_statements && (
          <div className="text-center py-8">
            <CText type="M14" color={colors.grayScale500}>
              No financial data available
            </CText>
          </div>
        )}
      </div>
    );
  };

  const renderManagementTab = () => {
    const insights = company?.company_insights;
    if (!insights) {
      return (
        <div className="text-center py-8">
          <CText type="M14" color={colors.grayScale500}>
            No management information available
          </CText>
        </div>
      );
    }

    return (
      <div>
        {renderInsightSection('Key Management Changes', insights.key_management_people_changes)}
        {renderInsightSection('Management Guidance', insights.management_guidance)}
        {renderInsightSection('Related Party Transactions', insights.related_party_transactions)}
      </div>
    );
  };

  const renderDocumentLink = (doc: any, idx: number) => {
    const url = doc.url || doc.pdf_url || doc.link;
    const title = doc.title || doc.name || doc.report_name || `Document ${idx + 1}`;
    const date = doc.date || doc.report_date || doc.year;

    return (
      <a
        key={idx}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-between p-3 rounded-lg mb-2 transition-colors"
        style={{
          backgroundColor: colors.inputBackground,
          border: `1px solid ${colors.borderColor}`,
        }}
      >
        <div className="flex items-center gap-3">
          {React.createElement(IoDocumentTextOutline as any, { size: 20, color: colors.primary })}
          <div>
            <CText type="M14" color={colors.textColor}>{title}</CText>
            {date && <CText type="r12" color={colors.grayScale400}>{date}</CText>}
          </div>
        </div>
        {React.createElement(FiExternalLink as any, { size: 16, color: colors.grayScale400 })}
      </a>
    );
  };

  const renderDocumentsTab = () => {
    if (!company) return null;

    const annualReports = Array.isArray(company.annual_reports_data) ? company.annual_reports_data : [];
    const concalls = Array.isArray(company.concalls_data) ? company.concalls_data : [];
    const announcements = Array.isArray(company.company_announcements_data) ? company.company_announcements_data : [];

    const hasDocuments = annualReports.length > 0 || concalls.length > 0 || announcements.length > 0;

    if (!hasDocuments) {
      return (
        <div className="text-center py-8">
          <CText type="M14" color={colors.grayScale500}>
            No documents available
          </CText>
        </div>
      );
    }

    return (
      <div>
        {annualReports.length > 0 && (
          <div className="mb-6">
            <CText type="M14" color={colors.primary} style={{ marginBottom: 12 }}>
              Annual Reports
            </CText>
            {annualReports.slice(0, 5).map((doc, idx) => renderDocumentLink(doc, idx))}
          </div>
        )}
        {concalls.length > 0 && (
          <div className="mb-6">
            <CText type="M14" color={colors.primary} style={{ marginBottom: 12 }}>
              Concall Transcripts
            </CText>
            {concalls.slice(0, 5).map((doc, idx) => renderDocumentLink(doc, idx))}
          </div>
        )}
        {announcements.length > 0 && (
          <div className="mb-6">
            <CText type="M14" color={colors.primary} style={{ marginBottom: 12 }}>
              Company Announcements
            </CText>
            {announcements.slice(0, 5).map((doc, idx) => renderDocumentLink(doc, idx))}
          </div>
        )}
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverviewTab();
      case 'financials':
        return renderFinancialsTab();
      case 'management':
        return renderManagementTab();
      case 'documents':
        return renderDocumentsTab();
      default:
        return null;
    }
  };

  return (
    <div
      className="p-4 lg:p-8 min-h-screen pb-24"
      style={{ backgroundColor: colors.backgroundColor }}
    >
      <div className="lg:max-w-6xl lg:mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-full transition-colors"
            style={{ backgroundColor: colors.inputBackground }}
          >
            {React.createElement(IoArrowBack as any, { size: 24, color: colors.textColor })}
          </button>
          <div className="flex-1">
            <CText type="B20" color={colors.textColor}>
              {loading ? 'Loading...' : company?.name || decodedCompanyName}
            </CText>
            {company && (
              <div className="flex items-center gap-2 mt-1">
                {company.ticker_symbol && (
                  <span
                    className="px-2 py-1 rounded text-xs font-medium"
                    style={{ backgroundColor: colors.primary, color: colors.white }}
                  >
                    {company.ticker_symbol}
                  </span>
                )}
                {company.sector && (
                  <span
                    className="px-2 py-1 rounded text-xs"
                    style={{ backgroundColor: colors.inputBackground, color: colors.grayScale500 }}
                  >
                    {company.sector}
                  </span>
                )}
                {company.industry && (
                  <span
                    className="px-2 py-1 rounded text-xs"
                    style={{ backgroundColor: colors.inputBackground, color: colors.grayScale400 }}
                  >
                    {company.industry}
                  </span>
                )}
              </div>
            )}
          </div>
          {company?.nse_link && (
            <a
              href={company.nse_link}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 rounded-lg transition-colors"
              style={{ backgroundColor: colors.primary, color: colors.white }}
            >
              NSE
            </a>
          )}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <CLoader />
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="text-center py-8">
            <CText type="M14" color={colors.alertColor}>{error}</CText>
            <button
              className="mt-4 px-4 py-2 rounded-lg"
              style={{ backgroundColor: colors.primary, color: colors.white }}
              onClick={() => window.location.reload()}
            >
              Retry
            </button>
          </div>
        )}

        {/* Content */}
        {!loading && !error && company && (
          <>
            {/* Key Metrics */}
            {renderMetricsCard()}

            {/* Tabs */}
            <div className="flex gap-2 overflow-x-auto pb-2 mb-4 scrollbar-hide">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl whitespace-nowrap transition-colors"
                  style={{
                    backgroundColor: activeTab === tab.id ? colors.primary : colors.inputBackground,
                    color: activeTab === tab.id ? colors.white : colors.grayScale400,
                  }}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {React.createElement(tab.icon as any, { size: 16 })}
                  <span className="text-sm font-medium">{tab.label}</span>
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div
              className="p-4 rounded-xl"
              style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.borderColor}` }}
            >
              {renderTabContent()}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CompanyDetail;
